import React, { useState, useCallback, useMemo } from 'react';
import { useApp, previewDocNo, hasPrefix } from '../../context/AppContext';
import { calculateLineItem, calculateInvoiceSummary, formatCurrency, today } from '../../utils/helpers';
import { validators, validateForm } from '../../utils/validation';
import FormField, { ErrorSummary } from './FormField';
import { Plus, Trash2, AlertTriangle } from 'lucide-react';

const emptyLine = { itemId: '', qty: 1, rate: 0, discountPercent: 0, taxPercent: 18, unit: '' };

export default function InvoiceForm({ type = 'sales', onSave, onCancel, initialData, nextEntryNo }) {
  const { state } = useApp();
  const [saving, setSaving] = useState(false);
  const isSales = type === 'sales' || type === 'salesReturn' || type === 'salesOrder';
  const isPurchaseBill = type === 'purchase';
  const isReturn = type === 'salesReturn' || type === 'purchaseReturn';
  const isChallan = type === 'challan';
  const isSalesInvoice = type === 'sales';
  // Purchase-side documents (bill / return / order) vs sales-side (invoice /
  // return / order / challan). Drives which items the line dropdown offers.
  const isPurchaseSide = type === 'purchase' || type === 'purchaseReturn' || type === 'purchaseOrder';

  // Only items enabled for this document's side appear in the item dropdown:
  // sales-side shows "Sales Item" toggles, purchase-side shows "Purchase Item".
  // `!== false` keeps legacy items (with the flag unset) visible.
  const selectableItems = useMemo(() => state.items.filter(i =>
    isPurchaseSide ? i.isPurchaseItem !== false : i.isSalesItem !== false
  ), [state.items, isPurchaseSide]);

  const parties = state.parties.filter(p =>
    isSales || isChallan ? (p.type === 'Customer' || p.type === 'Both') :
    (p.type === 'Supplier' || p.type === 'Both')
  );

  const [form, setForm] = useState(initialData || {
    date: today(),
    billDate: today(),
    partyId: '',
    billNo: '',
    dueDate: today(),
    narration: '',
    additionalCharges: 0,
    vehicleNo: '',
    driverName: '',
    againstInvoice: '',
    customerPoNo: '',
    customerPoDate: today(),
    shipToAddress: '',
    tdsSectionId: '',
  });

  const [lines, setLines] = useState(initialData?.items || [{ ...emptyLine }]);
  const [touched, setTouched] = useState({});
  const [submitted, setSubmitted] = useState(false);


  const selectedParty = state.parties.find(p => p.id === form.partyId);
  const isInterState = selectedParty && selectedParty.state !== state.company.state;

  // Document number to show: the saved one when editing, else a live preview
  // based on the selected date (picks the prefix effective for that date / FY).
  const NUM_CONFIG = {
    sales: { txn: 'salesInvoice', arr: 'salesInvoices', prefix: 'INV', field: 'invoiceNo' },
    salesReturn: { txn: 'salesReturn', arr: 'salesReturns', prefix: 'SR', field: 'returnNo' },
    salesOrder: { txn: 'salesOrder', arr: 'salesOrders', prefix: 'SO', field: 'orderNo' },
    challan: { txn: 'deliveryChallan', arr: 'deliveryChallans', prefix: 'DC', field: 'challanNo' },
    purchase: { txn: 'purchaseBill', arr: 'purchaseBills', prefix: 'PB', field: 'entryNo' },
    purchaseReturn: { txn: 'purchaseReturn', arr: 'purchaseReturns', prefix: 'PR', field: 'returnNo' },
    purchaseOrder: { txn: 'purchaseOrder', arr: 'purchaseOrders', prefix: 'PO', field: 'orderNo' },
  };
  const numCfg = NUM_CONFIG[type];
  // New entries require a Prefix Setting for this transaction (effective on the date).
  const prefixMissing = !!numCfg && !initialData && !hasPrefix(state, numCfg.txn, form.date);
  const displayedNo = useMemo(() => {
    if (numCfg && initialData && initialData[numCfg.field]) return initialData[numCfg.field];
    if (numCfg) return previewDocNo(state, numCfg.txn, numCfg.arr, form.date, numCfg.prefix);
    return nextEntryNo || '';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, initialData, form.date, state.prefixSettings, state.financialYears, nextEntryNo]);

  // For returns: list the selected party's original invoices/bills to pick from.
  const returnDocs = useMemo(() => {
    if (!isReturn) return [];
    if (isSales) {
      return state.salesInvoices
        .filter(i => !form.partyId || i.partyId === form.partyId)
        .map(i => ({ value: i.invoiceNo, label: `${i.invoiceNo} — ${formatCurrency(i.grandTotal)} (${i.date})` }));
    }
    return state.purchaseBills
      .filter(b => !form.partyId || b.partyId === form.partyId)
      .map(b => ({ value: b.entryNo, label: `${b.entryNo}${b.billNo ? ' / ' + b.billNo : ''} — ${formatCurrency(b.grandTotal)} (${b.date})` }));
  }, [isReturn, isSales, state.salesInvoices, state.purchaseBills, form.partyId]);

  const calculatedLines = useMemo(() => lines.map(calculateLineItem), [lines]);
  const summary = useMemo(() => calculateInvoiceSummary(calculatedLines, isInterState), [calculatedLines, isInterState]);

  // TDS applies to sales invoices (customer deducts TDS on our income) and
  // purchase bills (we deduct TDS when paying the supplier). Computed on the
  // taxable value (before GST), the standard base.
  const tdsApplicable = isSalesInvoice || isPurchaseBill;
  const tdsSection = (state.tdsSections || []).find(t => t.id === form.tdsSectionId);
  const tdsRate = tdsSection?.rate || 0;
  const tdsAmount = tdsApplicable && tdsSection ? Math.round((summary.taxable || 0) * tdsRate) / 100 : 0;
  const netAfterTds = (summary.grandTotal || 0) - tdsAmount;

  // ─── Header validation ───
  const headerErrors = useMemo(() => {
    const rules = {
      date: [validators.required(form.date, 'Date')],
      partyId: [validators.required(form.partyId, isSales || isChallan ? 'Customer' : 'Supplier')],
    };

    if (isPurchaseBill && !isReturn) {
      rules.billNo = [validators.required(form.billNo, 'Supplier bill no')];
      rules.billDate = [validators.required(form.billDate, 'Bill date')];
      if (form.dueDate && (form.billDate || form.date)) {
        rules.dueDate = [validators.dateAfter(form.dueDate, form.billDate || form.date, 'Due date', 'bill date')];
      }
    }

    if (isReturn) {
      rules.againstInvoice = [validators.required(form.againstInvoice, 'Against Invoice/Bill reference')];
    }

    if (isChallan) {
      rules.vehicleNo = [validators.vehicleNo(form.vehicleNo)];
    }

    if (form.narration) {
      rules.narration = [validators.maxLength(form.narration, 500, 'Narration')];
    }

    if (isPurchaseBill) {
      rules.additionalCharges = [validators.positiveNumber(form.additionalCharges, 'Additional charges')];
    }

    const { errors } = validateForm(rules);
    return errors;
  }, [form, isSales, isPurchaseBill, isReturn, isChallan]);

  // ─── Line item validation ───
  const validatedLines = useMemo(() => {
    const validItems = calculatedLines.filter(l => l.itemId);
    if (validItems.length === 0) return [];

    return calculatedLines.map((line, idx) => {
      if (!line.itemId) return { errors: {} };
      const item = state.items.find(i => i.id === line.itemId);
      const errs = {};

      // Duplicate item check
      const dupIndex = calculatedLines.findIndex((l, i) => i !== idx && l.itemId === line.itemId);
      if (dupIndex !== -1) {
        errs.itemId = `Duplicate item (also in row ${dupIndex + 1})`;
      }

      // Qty validation
      if (!line.qty || Number(line.qty) <= 0) {
        errs.qty = 'Qty must be > 0';
      } else if (Number(line.qty) > 999999) {
        errs.qty = 'Qty too large';
      }

      // Stock check for sales/challan
      if ((isSalesInvoice || isChallan) && !isReturn && item) {
        if (Number(line.qty) > item.currentStock) {
          errs.qty = `Insufficient stock (available: ${item.currentStock})`;
        }
      }

      // Rate validation
      if (Number(line.rate) <= 0) {
        errs.rate = 'Rate must be > 0';
      }

      // Discount range
      if (Number(line.discountPercent) < 0 || Number(line.discountPercent) > 100) {
        errs.discountPercent = '0-100%';
      }

      return { errors: errs };
    });
  }, [calculatedLines, state.items, isChallan, isReturn, isSalesInvoice]);

  const hasLineErrors = validatedLines.some(l => Object.keys(l.errors).length > 0);
  const validItemCount = calculatedLines.filter(l => l.itemId).length;
  const hasHeaderErrors = Object.values(headerErrors).some(e => e);
  const noItemsError = validItemCount === 0;
  const hasAnyErrors = hasHeaderErrors || hasLineErrors || noItemsError;

  const touch = (field) => setTouched(prev => ({ ...prev, [field]: true }));

  const addLine = () => setLines([...lines, { ...emptyLine }]);

  const removeLine = (idx) => {
    if (lines.length <= 1) return;
    setLines(lines.filter((_, i) => i !== idx));
  };

  const updateLine = useCallback((idx, field, value) => {
    setLines(prev => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: value };
      if (field === 'itemId') {
        const item = state.items.find(i => i.id === value);
        if (item) {
          updated[idx].rate = (isSalesInvoice || isChallan) ? item.saleRate : item.purchaseRate;
          updated[idx].taxPercent = item.taxPercent;
          updated[idx].unit = item.unit;
          updated[idx].itemName = item.name;
        }
      }
      return updated;
    });
  }, [state.items, isChallan, isSalesInvoice]);

  const handleSubmit = () => {
    setSubmitted(true);
    const allTouched = {};
    Object.keys(headerErrors).forEach(k => { allTouched[k] = true; });
    allTouched.partyId = true;
    allTouched.date = true;
    setTouched(allTouched);

    if (hasAnyErrors) return;
    if (prefixMissing) {
      alert('No Prefix Setting found for this transaction. Add one in Masters → Prefix Setting (effective on the selected date) before saving.');
      return;
    }

    setSaving(true);
    const data = {
      ...form,
      items: calculatedLines.filter(l => l.itemId),
      ...summary,
      isInterState,
      tdsSection: tdsSection?.section || '',
      tdsRate,
      tdsAmount,
      netAmount: netAfterTds,
    };
    onSave(data);
  };


  return (
    <div className="space-y-6">
      {submitted && hasAnyErrors && (
        <ErrorSummary errors={{
          ...headerErrors,
          ...(noItemsError ? { items: 'Please add at least one item with valid details' } : {}),
          ...(hasLineErrors ? { lineItems: 'Some line items have validation errors (see below)' } : {}),
        }} show={true} />
      )}

      {prefixMissing && (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <AlertTriangle size={18} className="mt-0.5 flex-shrink-0" />
          <span>No <strong>Prefix Setting</strong> is configured for this transaction (effective on the selected date). Add one in <strong>Masters → Prefix Setting</strong> — this entry can't be numbered or saved until then.</span>
        </div>
      )}

      {/* Header Fields */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {displayedNo && (
          <FormField label={
            type === 'sales' ? 'Invoice No' :
            type === 'salesReturn' ? 'Credit Note No' :
            type === 'challan' ? 'Challan No' :
            type === 'purchaseReturn' ? 'Debit Note No' :
            type === 'purchase' ? 'Purchase Bill No' :
            type === 'purchaseOrder' ? 'PO No' :
            'Entry No'
          }>
            <input className="input bg-gray-100 font-semibold text-blue-700" value={displayedNo} readOnly />
          </FormField>
        )}

        <FormField label={isPurchaseBill ? 'Entry Date' : 'Date'} required error={headerErrors.date} touched={touched.date || submitted}>
          <input className="input" type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} onBlur={() => touch('date')} max={today()} />
        </FormField>

        {isPurchaseBill && (
          <FormField label="Bill Date" required error={headerErrors.billDate} touched={touched.billDate || submitted} hint="Date on supplier's bill">
            <input className="input" type="date" value={form.billDate} onChange={e => setForm({...form, billDate: e.target.value})} onBlur={() => touch('billDate')} max={today()} />
          </FormField>
        )}

        <FormField label={isSales || isChallan ? 'Customer' : 'Supplier'} required error={headerErrors.partyId} touched={touched.partyId || submitted}>
          <select className="input" value={form.partyId} onChange={e => { const sp = state.parties.find(p => p.id === e.target.value); setForm({...form, partyId: e.target.value, againstInvoice: '', shipToAddress: sp?.shipToAddress || sp?.address || ''}); }} onBlur={() => touch('partyId')}>
            <option value="">-- Select --</option>
            {parties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </FormField>

        {isPurchaseBill && !isReturn && (
          <FormField label="Bill No (Supplier)" required error={headerErrors.billNo} touched={touched.billNo || submitted}>
            <input className="input" value={form.billNo} onChange={e => setForm({...form, billNo: e.target.value})} onBlur={() => touch('billNo')} maxLength={50} placeholder="Supplier's bill number" />
          </FormField>
        )}

        {isReturn && (
          <FormField label={isSales ? 'Against Invoice' : 'Against Bill'} required error={headerErrors.againstInvoice} touched={touched.againstInvoice || submitted} hint={!form.partyId ? `Select ${isSales ? 'customer' : 'supplier'} first` : (returnDocs.length === 0 ? `No ${isSales ? 'invoices' : 'bills'} for this party` : undefined)}>
            <select className="input" value={form.againstInvoice} onChange={e => setForm({...form, againstInvoice: e.target.value})} onBlur={() => touch('againstInvoice')} disabled={!form.partyId}>
              <option value="">-- Select --</option>
              {returnDocs.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
            </select>
          </FormField>
        )}

        {(isSales || isChallan) && !isReturn && (
          <>
            <FormField label="Customer PO No">
              <input className="input" value={form.customerPoNo} onChange={e => setForm({...form, customerPoNo: e.target.value})} maxLength={50} placeholder="Customer's PO number" />
            </FormField>
            <FormField label="Customer PO Date">
              <input className="input" type="date" value={form.customerPoDate} onChange={e => setForm({...form, customerPoDate: e.target.value})} max={today()} />
            </FormField>
          </>
        )}

        {isPurchaseBill && !isReturn && (
          <FormField label="Due Date" error={headerErrors.dueDate} touched={touched.dueDate || submitted} hint="Must be on or after bill date">
            <input className="input" type="date" value={form.dueDate} onChange={e => setForm({...form, dueDate: e.target.value})} onBlur={() => touch('dueDate')} min={form.billDate || form.date} />
          </FormField>
        )}

        {isChallan && (
          <>
            <FormField label="Vehicle No" error={headerErrors.vehicleNo} touched={touched.vehicleNo || submitted} hint="e.g. MH12AB1234">
              <input className="input" value={form.vehicleNo} onChange={e => setForm({...form, vehicleNo: e.target.value.toUpperCase()})} onBlur={() => touch('vehicleNo')} maxLength={15} style={{ textTransform: 'uppercase' }} />
            </FormField>
            <FormField label="Driver Name">
              <input className="input" value={form.driverName} onChange={e => setForm({...form, driverName: e.target.value})} maxLength={100} />
            </FormField>
          </>
        )}
      </div>

      {(isSales || isChallan) && (
        <FormField label="Ship To Address" hint="Defaults from the customer — edit to override for this document">
          <textarea className="input" rows={2} value={form.shipToAddress || ''} onChange={e => setForm({...form, shipToAddress: e.target.value})} maxLength={500} placeholder="Shipping address" />
        </FormField>
      )}

      {/* Warnings */}
      {selectedParty && isInterState && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-2 text-sm text-amber-800 flex items-center gap-2">
          <AlertTriangle size={16} />
          Inter-state supply detected ({state.company.state} &rarr; {selectedParty.state}). IGST will be applied.
        </div>
      )}

      {/* Line Items */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-700">
            Line Items
            {submitted && noItemsError && <span className="text-red-500 ml-2 font-normal text-xs">- At least one item is required</span>}
          </h3>
          <button onClick={addLine} className="btn btn-sm btn-secondary flex items-center gap-1">
            <Plus size={14} /> Add Row
          </button>
        </div>
        <div className="table-container">
          <table className="table text-xs min-w-[900px] [&_td]:!px-2 [&_th]:!px-2">
            <thead>
              <tr>
                <th style={{width: '28px'}}>#</th>
                <th style={{minWidth: '170px'}}>Item *</th>
                <th style={{width: '110px'}}>Qty *</th>
                <th style={{width: '55px'}}>Unit</th>
                <th style={{width: '130px'}}>Rate *</th>
                <th style={{width: '95px'}}>Disc%</th>
                <th style={{width: '110px'}}>Taxable</th>
                <th style={{width: '95px'}}>Tax%</th>
                <th style={{width: '110px'}}>Tax Amt</th>
                <th style={{width: '120px'}}>Net Amt</th>
                <th style={{width: '38px'}}></th>
              </tr>
            </thead>
            <tbody>
              {calculatedLines.map((line, idx) => {
                const le = validatedLines[idx]?.errors || {};
                const showLineErr = submitted;
                const item = state.items.find(i => i.id === line.itemId);
                return (
                  <tr key={idx} className={showLineErr && Object.keys(le).length > 0 ? '!bg-red-50' : ''}>
                    <td>{idx + 1}</td>
                    <td>
                      <select
                        className={`input !py-1 !text-xs ${showLineErr && le.itemId ? '!border-red-400' : ''}`}
                        value={line.itemId}
                        onChange={e => updateLine(idx, 'itemId', e.target.value)}
                      >
                        <option value="">-- Select Item --</option>
                        {(item && !selectableItems.some(si => si.id === item.id) ? [item, ...selectableItems] : selectableItems).map(i => (
                          <option key={i.id} value={i.id}>
                            {i.name} {(isSalesInvoice || isChallan) && !isReturn ? `[Stock: ${i.currentStock}]` : ''}
                          </option>
                        ))}
                      </select>
                      {showLineErr && le.itemId && <p className="text-red-500 text-[10px] mt-0.5">{le.itemId}</p>}
                    </td>
                    <td>
                      <input
                        className={`input !py-1 !px-2 !text-xs text-right ${showLineErr && le.qty ? '!border-red-400' : ''}`}
                        type="number"
                        min="1"
                        max={item && (isSalesInvoice || isChallan) && !isReturn ? item.currentStock : 999999}
                        value={line.qty}
                        onChange={e => updateLine(idx, 'qty', Number(e.target.value))}
                      />
                      {showLineErr && le.qty && <p className="text-red-500 text-[10px] mt-0.5">{le.qty}</p>}
                    </td>
                    <td className="text-gray-500">{line.unit || '-'}</td>
                    <td>
                      <input
                        className={`input !py-1 !px-2 !text-xs text-right ${showLineErr && le.rate ? '!border-red-400' : ''}`}
                        type="number"
                        min="0.01"
                        step="0.01"
                        value={line.rate}
                        onChange={e => updateLine(idx, 'rate', Number(e.target.value))}
                      />
                      {showLineErr && le.rate && <p className="text-red-500 text-[10px] mt-0.5">{le.rate}</p>}
                    </td>
                    <td>
                      <input
                        className={`input !py-1 !px-2 !text-xs text-right ${showLineErr && le.discountPercent ? '!border-red-400' : ''}`}
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        value={line.discountPercent}
                        onChange={e => updateLine(idx, 'discountPercent', Number(e.target.value))}
                      />
                    </td>
                    <td className="text-right">{formatCurrency(line.taxableAmount)}</td>
                    <td>
                      <select className="input !py-1 !text-xs" value={line.taxPercent} onChange={e => updateLine(idx, 'taxPercent', Number(e.target.value))}>
                        {state.taxes.map(t => <option key={t.id} value={t.rate}>{t.rate}%</option>)}
                      </select>
                    </td>
                    <td className="text-right">{formatCurrency(line.taxAmount)}</td>
                    <td className="text-right font-medium">{formatCurrency(line.netAmount)}</td>
                    <td>
                      <button onClick={() => removeLine(idx)} disabled={lines.length <= 1} className="p-1 rounded hover:bg-red-50 text-red-400 hover:text-red-600 disabled:opacity-30 disabled:cursor-not-allowed">
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Summary */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4">
        <div className="flex-1 md:mr-8 w-full">
          <FormField label="Narration" error={headerErrors.narration} touched={touched.narration || submitted}>
            <textarea className="input" rows={3} value={form.narration} onChange={e => setForm({...form, narration: e.target.value})} onBlur={() => touch('narration')} maxLength={500} placeholder="Notes..." />
          </FormField>
          {isPurchaseBill && (
            <div className="mt-3">
              <FormField label="Additional Charges (Freight, Labour, etc.)" error={headerErrors.additionalCharges} touched={touched.additionalCharges || submitted}>
                <input className="input max-w-xs" type="number" min="0" step="0.01" value={form.additionalCharges} onChange={e => setForm({...form, additionalCharges: Number(e.target.value)})} onBlur={() => touch('additionalCharges')} />
              </FormField>
            </div>
          )}
        </div>
        <div className="w-full md:w-72 bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
          <div className="flex justify-between"><span className="text-gray-600">Subtotal:</span><span>{formatCurrency(summary.subtotal)}</span></div>
          {summary.totalDiscount > 0 && (
            <div className="flex justify-between"><span className="text-gray-600">Discount:</span><span className="text-red-600">-{formatCurrency(summary.totalDiscount)}</span></div>
          )}
          <div className="flex justify-between"><span className="text-gray-600">Taxable:</span><span>{formatCurrency(summary.taxable)}</span></div>
          {isInterState ? (
            <div className="flex justify-between"><span className="text-gray-600">IGST:</span><span>{formatCurrency(summary.igst)}</span></div>
          ) : (
            <>
              <div className="flex justify-between"><span className="text-gray-600">CGST:</span><span>{formatCurrency(summary.cgst)}</span></div>
              <div className="flex justify-between"><span className="text-gray-600">SGST:</span><span>{formatCurrency(summary.sgst)}</span></div>
            </>
          )}
          {summary.roundOff !== 0 && (
            <div className="flex justify-between"><span className="text-gray-600">Round Off:</span><span>{formatCurrency(summary.roundOff)}</span></div>
          )}
          <div className="flex justify-between pt-2 border-t border-gray-300 font-bold text-base">
            <span>Grand Total:</span>
            <span>{formatCurrency(summary.grandTotal)}</span>
          </div>

          {tdsApplicable && (
            <div className="pt-2 mt-2 border-t border-gray-200 space-y-1.5">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  {isSalesInvoice ? 'TDS deducted by customer (on income)' : 'TDS to deduct (on payment)'}
                </label>
                <select className="input !py-1.5" value={form.tdsSectionId} onChange={e => setForm({ ...form, tdsSectionId: e.target.value })}>
                  <option value="">-- No TDS --</option>
                  {(state.tdsSections || []).map(t => (
                    <option key={t.id} value={t.id}>{t.section} · {t.name} ({t.rate}%)</option>
                  ))}
                </select>
              </div>
              {tdsSection && (
                <>
                  <div className="flex justify-between text-sm"><span className="text-gray-600">TDS @ {tdsRate}% ({tdsSection.section}):</span><span className="text-red-600">-{formatCurrency(tdsAmount)}</span></div>
                  <div className="flex justify-between font-semibold text-green-700">
                    <span>{isSalesInvoice ? 'Net Receivable:' : 'Net Payable:'}</span><span>{formatCurrency(netAfterTds)}</span>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-4 border-t">
        <button onClick={onCancel} className="btn btn-secondary">Cancel</button>
        <button data-keyboard-save onClick={handleSubmit} className="btn btn-primary" disabled={(submitted && hasAnyErrors) || saving || prefixMissing}>Save</button>
      </div>
    </div>
  );
}
